import { formatTimestamp } from '@lightdash/common';
import {
    ActionIcon,
    Button,
    CopyButton,
    Select,
    Stack,
    TextInput,
    Tooltip,
} from '@mantine-8/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconCopy, IconKey } from '@tabler/icons-react';
import { type FC } from 'react';
import useHealth from '../../../hooks/health/useHealth';
import { useCreateAccessToken } from '../../../hooks/useAccessToken';
import Callout from '../../common/Callout';
import MantineIcon from '../../common/MantineIcon';
import MantineModal from '../../common/MantineModal';
import { useExpireOptions } from './useExpireOptions';

export const CreateTokenModal: FC<{
    onBackClick: () => void;
}> = ({ onBackClick }) => {
    const health = useHealth();

    const {
        data,
        mutate: createAccessToken,
        isLoading,
        isSuccess,
    } = useCreateAccessToken();

    const expireOptions = useExpireOptions(true);

    const form = useForm({
        initialValues: {
            description: '',
            expiresAt: expireOptions[0].value,
        },
    });

    const handleOnSubmit = form.onSubmit(({ description, expiresAt }) => {
        const currentDate = new Date();
        const dateWhenExpires = !!Number(expiresAt)
            ? new Date(
                  currentDate.setDate(
                      currentDate.getDate() + Number(expiresAt),
                  ),
              )
            : undefined;

        createAccessToken({
            description,
            expiresAt: dateWhenExpires ?? null,
            autoGenerated: false,
        });
    });

    return (
        <MantineModal
            opened
            onClose={onBackClick}
            title={data ? 'Your token has been generated' : 'New token'}
            cancelLabel={isSuccess ? 'Close' : 'Cancel'}
            icon={IconKey}
            actions={
                !isSuccess ? (
                    <Button
                        type="submit"
                        form="create-token-form"
                        loading={isLoading}
                    >
                        Generate token
                    </Button>
                ) : undefined
            }
        >
            {!isSuccess ? (
                <form id="create-token-form" onSubmit={handleOnSubmit}>
                    <Stack>
                        <TextInput
                            label="What's this token for?"
                            disabled={isLoading}
                            placeholder="Description"
                            required
                            {...form.getInputProps('description')}
                        />

                        <Select
                            defaultValue={expireOptions[0].value}
                            label="Expiration"
                            data={expireOptions}
                            required
                            disabled={isLoading}
                            {...form.getInputProps('expiresAt')}
                        />
                    </Stack>
                </form>
            ) : (
                <Stack gap="md">
                    <TextInput
                        id="invite-link-input"
                        label="Token"
                        readOnly
                        className="sentry-block ph-no-capture"
                        value={data.token}
                        rightSection={
                            <CopyButton value={data.token}>
                                {({ copied, copy }) => (
                                    <Tooltip
                                        label={copied ? 'Copied' : 'Copy'}
                                        withArrow
                                        position="right"
                                    >
                                        <ActionIcon
                                            color={copied ? 'teal' : 'gray'}
                                            onClick={copy}
                                        >
                                            <MantineIcon
                                                icon={
                                                    copied
                                                        ? IconCheck
                                                        : IconCopy
                                                }
                                            />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                            </CopyButton>
                        }
                    />
                    <TextInput
                        id="invite-link-input"
                        label="CLI Authentication code"
                        className="sentry-block ph-no-capture"
                        readOnly
                        value={`lightdash login ${health.data?.siteUrl} --token ${data.token}`}
                        rightSection={
                            <CopyButton
                                value={`lightdash login ${health.data?.siteUrl} --token ${data.token}`}
                            >
                                {({ copied, copy }) => (
                                    <Tooltip
                                        label={copied ? 'Copied' : 'Copy'}
                                        withArrow
                                        position="right"
                                    >
                                        <ActionIcon
                                            color={copied ? 'teal' : 'gray'}
                                            onClick={copy}
                                        >
                                            <MantineIcon
                                                icon={
                                                    copied
                                                        ? IconCheck
                                                        : IconCopy
                                                }
                                            />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                            </CopyButton>
                        }
                    />
                    <Callout variant="info">
                        {data.expiresAt &&
                            `This token will expire on ${formatTimestamp(
                                data.expiresAt,
                            )}. `}
                        Make sure to copy your personal access token now. You
                        won't be able to see it again!
                    </Callout>
                </Stack>
            )}
        </MantineModal>
    );
};
